import ItemApi from '@apis/item/ItemApi';
import queries from '@apis/queries';
import useShop from '@hooks/shop/useShop';
import useToast from '@hooks/ui/useToast';
import useProjectId from '@hooks/useProjectId';
import { CartStatus } from '@models/cart';
import { ItemResponse } from '@models/item';
import {
    CreateOrderRequest,
    OptionWithGroup, OrderLine, OrderOption, RecentOrderItemRow, RecentOrderOptionRow, RecentOrderRow,
} from '@models/order';
import orderWithTotalAmount from '@providers/OrderProvider/utils/orderWithTotalAmount';
import { useFlow } from '@router/stackflow';
import { orderActions } from '@store/order/orderSlice';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDispatch } from 'react-redux';

function useDoRecentOrder(order: RecentOrderRow) {
    const {
        orderItems, shopId, shopName, orderNo,
    } = order;
    const { data: orderAble } = useQuery(queries.orders.orderAble(orderNo));
    const projectId = useProjectId();
    const { push } = useFlow();

    const shop = useShop(shopId);

    const toast = useToast();
    const dispatch = useDispatch();

    const generateOptions = (
        orderOptions: RecentOrderOptionRow[],
        item: ItemResponse,
        optionType: 'PERSONAL' | 'DEFAULT',
    ): OptionWithGroup[] => {
        const { defaultOptionGroups, personalOptionGroups } = item;
        const optionGroups = optionType === 'DEFAULT'
            ? defaultOptionGroups : personalOptionGroups;
        const availableOptions = optionGroups
            .map((group) => group.options)
            .flat();

        const options: OptionWithGroup[] = [];
        for (let j = 0; j < orderOptions.length; j += 1) {
            const { optionId, qty, posId } = orderOptions[j];
            const itemOption = availableOptions.find((opt) => opt.optionId === optionId);
            if (itemOption) {
                const { name, salePrice, maxQuantity } = itemOption;
                const optionGroup = optionGroups.find(
                    (group) => group.options.map((opt) => opt.optionId).includes(optionId),
                )!;
                const newOption: OrderOption = {
                    shopOptionId: optionId,
                    optionName: name,
                    optionPrice: salePrice,
                    quantity: qty,
                    totalAmount: salePrice * qty,
                    maxQuantity,
                    posId,
                };
                options.push({
                    optionGroupId: optionGroup.optionGroupId,
                    maxSelection: optionGroup.maxSelection,
                    minSelection: optionGroup.minSelection,
                    options: [newOption],
                });
            }
        }
        return options;
    };

    // 모든 기본 옵션에 대해 주문한 옵션중에 포함되었는지 확인 (필수 옵션이 추가되었을 때)
    const getNotIncludeDefaultOption = (
        item: ItemResponse,
        orderItem: RecentOrderItemRow,
    ) => {
        const orderedOptionIds = orderItem
            .options.map((it) => it.optionId);

        return item.defaultOptionGroups
            .filter((it) => it.minSelection > 0)
            .every((it) => {
                const optionIds = it.options.map((option) => option.optionId);
                return optionIds.some((id) => orderedOptionIds.includes(id));
            });
    };

    const generateOrderLines = async () => {
        const orderLines: OrderLine[] = [];
        for (let i = 0; i < orderItems.length; i += 1) {
            const { itemId, qty, options: orderOptions } = orderItems[i];
            const item = await ItemApi.getItem(itemId);
            const {
                name, salePrice, imageUrls,
            } = item;
            if (item.isSoldOut) {
                throw new Error('상품이 품절되었습니다.');
            }

            const defaultNotIncluded = getNotIncludeDefaultOption(item, orderItems[i]);
            if (defaultNotIncluded) {
                throw new Error('현재 주문 불가능한 옵션이 포함되어 있습니다.');
            }

            const defaultOptions = generateOptions(orderOptions, item, 'DEFAULT');
            const personalOptions = generateOptions(orderOptions, item, 'PERSONAL');
            const options = [...defaultOptions, ...personalOptions]
                .map((optionGroup) => optionGroup.options)
                .flat();

            const optionAmount = options
                .map((option) => option.totalAmount)
                .reduce((prev, curr) => prev + curr, 0);
            const totalAmount = (salePrice + optionAmount) * qty;
            const orderItem: OrderLine = {
                shopItemId: itemId,
                itemName: name,
                itemPrice: salePrice,
                status: CartStatus.ON_SALE,
                quantity: qty,
                defaultOptions,
                personalOptions,
                itemImageUrl: imageUrls?.[0],
                thumbnailImage: imageUrls?.[0],
                totalAmount,
                couponUsable: item.couponUsable,
                posId: item.posId,
            };
            orderLines.push(orderItem);
        }
        return orderLines;
    };

    const generateRequest = async () => {
        const orderLines = await generateOrderLines();
        const totalAmount = orderLines
            .map((orderLine) => orderLine.totalAmount)
            .reduce((prev, curr) => prev + curr, 0);

        const options = orderItems.map((orderItem) => orderItem.options)
            .flat();
        const parsed = orderLines.map(
            (line) => [...line.personalOptions, ...line.defaultOptions].flat()
                .map((group) => group.options).flat(),
        ).flat();

        if (options.length > parsed.length) {
            throw new Error('현재 주문 불가능한 옵션이 포함되어 있습니다.');
        }

        const request: CreateOrderRequest = {
            projectId,
            shopId,
            shopName,
            orderPath: 'DIRECT',
            orderType: 'TAKE_OUT',
            orderLines,
            totalAmount,
            totalDiscount: 0,
            paymentAmount: totalAmount,
            paymentMethod: 'CREDIT_CARD',
            returnUrl: '',
            totalCouponDiscount: 0,
            totalGiftDiscount: 0,
            totalPointDiscount: 0,
        };
        return orderWithTotalAmount(request);
    };

    return async () => {
        if (shop?.orderStatus !== 'OPEN') {
            push('NotOperationModal', {});
            return;
        }

        console.log({ orderAble });
        if (!orderAble?.canOrder) {
            push('ItemSoldOutModal', { shopId });
            return;
        }
        try {
            const request = await generateRequest();
            dispatch(orderActions.copy(request));
            push('OrderActivity', { });
        } catch (e) {
            console.error(e);
            // eslint-disable-next-line no-unsafe-optional-chaining
            if (e instanceof AxiosError && 'message' in e.response?.data) {
                toast.error(e.response?.data.message as string);
            } else if (e instanceof Error) {
                toast.error(e.message);
            } else {
                toast.error('알 수 없는 오류가 발생하였습니다.');
            }
        }
    };
}

export default useDoRecentOrder;
